/**
 * Extract filename from a URL, handling complex URLs with query parameters.
 * @param {string} url - The URL to extract the filename from.
 * @returns {string} The extracted filename or a fallback filename.
 */
export function extractFilenameFromUrl(url: string): string {
	try {
		// Remove query parameters first
		const urlWithoutQuery = url.split("?")[0];

		// Split by '/' and get the last part
		const pathParts = urlWithoutQuery.split("/");
		const lastPart = pathParts[pathParts.length - 1];

		// If the last part contains a file extension, check if it starts with 'file'
		if (lastPart && lastPart.includes(".")) {
			// Split by dots to check the last segment
			const dotParts = lastPart.split(".");
			const lastSegment = dotParts[dotParts.length - 1];

			// If the last segment starts with 'file', use the entire lastPart
			if (lastSegment && lastSegment.startsWith("file")) {
				return lastPart;
			}

			// Otherwise, change the first part to 'file' and keep the rest
			dotParts[0] = "file";
			return dotParts.join(".");
		}

		// If no extension found, look for parts that might be filenames
		for (let i = pathParts.length - 1; i >= 0; i--) {
			const part = pathParts[i];
			if (part && part.includes(".") && part.length > 5) {
				// Check if this part's last segment starts with 'file'
				const dotParts = part.split(".");
				const lastSegment = dotParts[dotParts.length - 1];
				if (lastSegment && lastSegment.startsWith("file")) {
					return part;
				}
				// Otherwise, change the first part to 'file' and keep the rest
				dotParts[0] = "file";
				return dotParts.join(".");
			}
		}

		// Fallback to a generic filename
		return "file";
	} catch (error) {
		console.warn("Failed to extract filename from URL:", url, error);
		return "file";
	}
}

function isSignedDownloadUrl(rawUrl: string): boolean {
	try {
		const parsed = new URL(rawUrl);
		const params = parsed.searchParams;
		const hasParam = (key: string) => params.has(key);

		const hasAwsSignature = hasParam("X-Amz-Signature") || hasParam("X-Amz-Credential") || hasParam("X-Amz-Algorithm");
		const hasGoogleSignature = hasParam("X-Goog-Signature") || hasParam("X-Goog-Credential");
		const hasAzureSignature = hasParam("sig") && (hasParam("se") || hasParam("sp") || hasParam("sr") || hasParam("sv"));

		return hasAwsSignature || hasGoogleSignature || hasAzureSignature;
	} catch (error) {
		return false;
	}
}

export function buildDownloadUrl(url: string): string {
	if (url.startsWith("blob:") || isSignedDownloadUrl(url)) {
		return url;
	}
	const separator = url.includes("?") ? "&" : "?";
	return `${url}${separator}timestamp=${new Date().getTime()}`;
}

/**
 * LSP-23301: every download fetch skips the browser's HTTP cache, for both reading and writing.
 * A user's Chrome profile with a broken disk-cache entry got `200`/`304` headers for a 45 MB PDF and
 * then failed reading the body (`TypeError: Failed to fetch`) on every attempt, while Incognito (a
 * separate in-memory cache) succeeded. The bytes go straight into a saved file or zip, so caching
 * them buys nothing.
 */
export const DOWNLOAD_FETCH_OPTIONS: RequestInit = { cache: "no-store" };

/**
 * LSP-23307: the wait before each retry of a download, so a file gets `length + 1` attempts in all.
 * On a slow connection (slow-2g, ~30 files streaming at once for 6-11 minutes each) storage answers
 * 200 and then one body drops mid-stream (`response.blob()` -> `TypeError: Failed to fetch`, Safari
 * `Load failed`), which used to lose the whole zip. A fresh request usually gets through.
 */
export const DOWNLOAD_RETRY_DELAYS_MS: readonly number[] = [250, 750];

/** A download answered with a non-ok status. The message keeps the long-standing `Failed to fetch <url>: <statusText>` shape. */
export class DownloadResponseError extends Error {
	readonly status: number;
	readonly statusText: string;
	readonly attempt: number;

	constructor(downloadUrl: string, response: Response, attempt: number) {
		super(`Failed to fetch ${downloadUrl}: ${response.statusText}`);
		this.name = "DownloadResponseError";
		this.status = response.status;
		this.statusText = response.statusText;
		this.attempt = attempt;
	}
}

/** Attempt number and response status of a thrown (network or body) failure, which carries neither itself. */
const thrownFailureDetails = new WeakMap<object, { attempt: number; status?: number }>();

/**
 * Plain fields describing a download failure for a log line. An Error serializes as `{}` in Datadog,
 * so its name and message have to travel as strings (LSP-23307).
 */
export function describeDownloadFailure(error: unknown): {
	errorName: string;
	errorMessage: string;
	status?: number;
	statusText?: string;
	attempt?: number;
} {
	if (error instanceof DownloadResponseError) {
		return {
			errorName: error.name,
			errorMessage: error.message,
			status: error.status,
			statusText: error.statusText,
			attempt: error.attempt
		};
	}
	const thrownDetails = typeof error === "object" && error !== null ? thrownFailureDetails.get(error) : undefined;
	return {
		errorName: error instanceof Error ? error.name : typeof error,
		errorMessage: error instanceof Error ? error.message : String(error),
		...(thrownDetails?.status === undefined ? {} : { status: thrownDetails.status }),
		...(thrownDetails === undefined ? {} : { attempt: thrownDetails.attempt })
	};
}

function isRetryableDownloadFailure(downloadUrl: string, error: unknown): boolean {
	// A blob: URL is local memory: if it failed, it was revoked and will never come back.
	if (downloadUrl.startsWith("blob:")) return false;
	// A 4xx (an expired signed URL's 403, a deleted file's 404) answers the same way every time.
	if (error instanceof DownloadResponseError) return error.status >= 500;
	return true;
}

const waitFor = (delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs));

/**
 * Fetch a download's bytes, retrying a dropped connection or body read and a 5xx answer
 * (`DOWNLOAD_RETRY_DELAYS_MS`). A 4xx answer and a `blob:` URL fail on the first attempt. A non-ok
 * answer rejects with `DownloadResponseError`; a thrown failure rejects with the original error.
 */
export async function fetchDownloadBlob(downloadUrl: string, options: { retryDelaysMs?: readonly number[] } = {}): Promise<Blob> {
	const retryDelaysMs = options.retryDelaysMs ?? DOWNLOAD_RETRY_DELAYS_MS;
	const maxAttempts = retryDelaysMs.length + 1;
	for (let attempt = 1; ; attempt += 1) {
		let status: number | undefined;
		try {
			const response = await fetch(downloadUrl, DOWNLOAD_FETCH_OPTIONS);
			status = response.status;
			if (!response.ok) {
				// Release the unread error body so a retry (up to 50 files at once) doesn't keep its connection busy.
				void response.body?.cancel().catch(() => undefined);
				throw new DownloadResponseError(downloadUrl, response, attempt);
			}
			return await response.blob();
		} catch (error) {
			if (typeof error === "object" && error !== null && !(error instanceof DownloadResponseError)) {
				thrownFailureDetails.set(error, { attempt, ...(status === undefined ? {} : { status }) });
			}
			const failure = { downloadUrl, maxAttempts, ...describeDownloadFailure(error), attempt };
			if (attempt >= maxAttempts || !isRetryableDownloadFailure(downloadUrl, error)) {
				console.warn("fetchDownloadBlob: download failed; not retrying", failure);
				throw error;
			}
			const retryDelayMs = retryDelaysMs[attempt - 1];
			console.warn("fetchDownloadBlob: download attempt failed; retrying", { ...failure, retryDelayMs });
			await waitFor(retryDelayMs);
		}
	}
}

export function withBlobExtension(url: string, fileName: string, blob: Blob): string {
	if (!url.startsWith("blob:")) return fileName;
	const extension = blob.type === "image/jpeg" ? "jpg" : blob.type.match(/^image\/([a-z0-9-]+)$/)?.[1];
	if (!extension) return fileName;
	return `${fileName.replace(/\.(?:jpe?g|png|webp|gif|avif|bmp|tiff?|heic|heif)$/i, "")}.${extension}`;
}

/**
 * How long the blob URL is kept alive after the click. WebKit begins the download in a later task,
 * so revoking in the same task cancels it outright (LSP-16021). Chromium/Firefox do not need the
 * delay but are unharmed by it; the URL is revoked either way, so nothing leaks.
 */
const BLOB_URL_REVOKE_DELAY_MS = 60_000;

/**
 * LSP-16021: iOS Safari would not download; it opened the PDF in the browser and left the user to
 * find Share -> Save to Files. Two causes, both here:
 *
 *  1. The blob kept its original `application/pdf` type. WebKit renders a blob: URL whose MIME it
 *     can display rather than treating it as a download, so `download` was ignored and the anchor
 *     behaved like a navigation. Re-typing the bytes as application/octet-stream leaves WebKit
 *     nothing to render, so it downloads and shows its own save prompt. The extension in `fileName`
 *     still drives which app opens the saved file, so nothing about the result changes.
 *  2. The object URL was revoked synchronously after click(), which cancels the download WebKit had
 *     not started yet.
 *
 * Single implementation so the zip path and the single-file path cannot drift apart again.
 */
export function saveBlobAsFile(blob: Blob, fileName: string): void {
	if (typeof document === "undefined") {
		return;
	}

	const downloadableBlob = blob.type === "application/octet-stream" ? blob : new Blob([blob], { type: "application/octet-stream" });
	const objectUrl = URL.createObjectURL(downloadableBlob);
	const link = document.createElement("a");
	link.href = objectUrl;
	link.download = fileName;
	link.style.display = "none";
	// Keep the navigation in this tab: a blob: URL opened in a new tab is the inline-preview
	// behaviour this fix exists to avoid.
	link.rel = "noopener";

	const canAppendToBody = !!document.body;
	if (canAppendToBody) {
		document.body.appendChild(link);
	}

	link.click();

	if (canAppendToBody) {
		document.body.removeChild(link);
	}

	setTimeout(() => URL.revokeObjectURL(objectUrl), BLOB_URL_REVOKE_DELAY_MS);
}

/**
 * LSP-23307: the last resort for a strict single-file download whose bytes never arrived after every
 * retry (a 4xx never gets here). The browser fetches the file itself in a new tab, on its own single
 * connection, and saves it, shows it, or shows its own error there. `true` means the user can see that
 * tab, not that the bytes arrived. The anchor fallback cannot be used: a cross-origin `download` link
 * leaves nothing on screen, so a failure would read as success (LSP-17228).
 * Two cases where a tab cannot help are reported as failure, so the caller's toast and kept selection
 * still apply: an offline browser (the tab could only show an offline page) and a blocked tab
 * (`window.open` returns null). "noopener" is not passed because it makes `window.open` return null
 * even when the tab opened.
 */
function openInNewTab(failure: { downloadUrl: string } & Record<string, unknown>): boolean {
	if (typeof navigator !== "undefined" && navigator.onLine === false) {
		console.warn("downloadFile: the browser is offline; reporting the download as failed instead of opening a tab", failure);
		return false;
	}
	const newTab = window.open(failure.downloadUrl, "_blank");
	if (!newTab) {
		console.warn("downloadFile: the new tab was blocked; reporting the download as failed", failure);
		return false;
	}
	newTab.opener = null;
	console.warn("downloadFile: fetch/blob download failed; opening the file in a new tab", failure);
	return true;
}

export async function downloadFile(
	url: string,
	fileName: string,
	options: {
		failOnRejectedSignedUrl?: boolean;
		failOnRejectedResponse?: boolean;
		/** With `failOnRejectedResponse`: after every retry has thrown, open the file in a new tab (LSP-23307) instead of refusing. */
		openInNewTabWhenFetchFails?: boolean;
	} = {}
): Promise<boolean> {
	try {
		if (!fileName) {
			fileName = extractFilenameFromUrl(url);
		}

		// Try fetch + blob approach (creates a same-origin blob URL so the download attribute works)
		const signedDownloadUrl = isSignedDownloadUrl(url);
		const downloadUrl = signedDownloadUrl ? url : buildDownloadUrl(url);
		const failClosedOnRejectedResponse = options.failOnRejectedResponse || (signedDownloadUrl && options.failOnRejectedSignedUrl);
		try {
			const blob = await fetchDownloadBlob(downloadUrl);
			if (!blob) throw new Error(`Empty response from ${downloadUrl}`);
			// Shared with the zip path: forces a download-typed blob and defers the revoke, both of
			// which iOS Safari needs (LSP-16021).
			saveBlobAsFile(blob, withBlobExtension(url, fileName, blob));
			return true;
		} catch (error) {
			const failure = { downloadUrl, error, ...describeDownloadFailure(error) };
			if (error instanceof DownloadResponseError && failClosedOnRejectedResponse) {
				console.warn("downloadFile: rejected response; refusing direct download fallback", { ...failure, signedDownloadUrl });
				return false;
			}
			if (options.failOnRejectedResponse && options.openInNewTabWhenFetchFails && !url.startsWith("blob:")) {
				return openInNewTab(failure);
			}
			if (url.startsWith("blob:") || options.failOnRejectedResponse) {
				console.warn("downloadFile: fetch/blob download failed; refusing direct download fallback", failure);
				return false;
			}
			// Fetch, blob, and browser failures can still succeed as a direct download.
			console.warn("downloadFile: fetch/blob download failed; trying a direct download", failure);
		}

		// Fallback: direct link for signed URLs when CORS blocks fetch
		const a = document.createElement("a");
		a.style.display = "none";
		a.href = downloadUrl;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		return true;
	} catch (err) {
		console.error(err, {
			tags: {
				method: "downloadFile",
				file: "download.js"
			},
			extra: {
				fileUrl: url
			}
		});
		return false;
	}
}
